import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationBell } from './NotificationBell';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import { useAuth } from '../context/AuthContext';
import { getRegionStats } from '../services/api';
import {
  LayoutDashboard, Briefcase, Users, Video, BarChart3,
  Settings, ChevronLeft, ChevronRight, MapPin, ArrowLeftRight, Globe2,
  Shield, Mail, UserCog, LogOut, Plug, FileSearch, ClipboardCheck, Radar
} from 'lucide-react';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/', roles: ['admin', 'recruiter', 'viewer'] },
  { icon: Briefcase, label: 'Jobs', path: '/jobs', roles: ['admin', 'recruiter', 'viewer'] },
  { icon: Users, label: 'Candidates', path: '/candidates', roles: ['admin', 'recruiter'] },
  { icon: Video, label: 'Interviews', path: '/interviews', roles: ['admin', 'recruiter'] },
  { icon: FileSearch, label: 'Pre-Screening', path: '/pre-screening', roles: ['admin', 'recruiter'] },
  { icon: Radar, label: 'Scout', path: '/scout', roles: ['admin', 'recruiter', 'viewer'] },
  { icon: ClipboardCheck, label: 'Assessments', path: '/assessments', roles: ['admin', 'recruiter', 'viewer'] },
  { icon: Mail, label: 'Emails', path: '/emails', roles: ['admin', 'recruiter'] },
  { icon: BarChart3, label: 'Analytics', path: '/analytics', roles: ['admin', 'recruiter', 'viewer'] },
  { icon: UserCog, label: 'Users', path: '/users', roles: ['admin'] },
  { icon: Plug, label: 'API Settings', path: '/integrations', roles: ['admin'] },
  { icon: Shield, label: 'Audit Log', path: '/audit-log', roles: ['admin'] },
  { icon: Settings, label: 'Settings', path: '/settings', roles: ['admin'] },
];

const REGION_COLORS = {
  NA: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', dot: 'bg-blue-400' },
  UK_EMEA: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
  APAC: { bg: 'bg-violet-500/15', text: 'text-violet-400', border: 'border-violet-500/30', dot: 'bg-violet-400' },
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { region, clearRegion, regionLabel } = useRegion();
  const { user, logout } = useAuth();
  const colors = REGION_COLORS[region] || REGION_COLORS.APAC;

  const visibleNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-full bg-card border-r z-40 flex flex-col transition-all duration-300 ${
        collapsed ? 'w-[68px]' : 'w-[240px]'
      }`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 h-16 border-b shrink-0">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-sm">H</span>
        </div>
        {!collapsed && (
          <span className="font-bold text-base tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            HireFast AI
          </span>
        )}
      </div>

      {/* Region Indicator */}
      <div className={`px-3 py-2.5 border-b ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <button
            onClick={clearRegion}
            className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center cursor-pointer`}
            title={`Switch from ${regionLabel}`}
            data-testid="region-switch-collapsed"
          >
            <Globe2 className={`h-4 w-4 ${colors.text}`} strokeWidth={1.8} />
          </button>
        ) : (
          <button
            onClick={clearRegion}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border ${colors.border} ${colors.bg} cursor-pointer transition-all hover:opacity-80`}
            data-testid="region-switch-btn"
          >
            <div className={`w-2 h-2 rounded-full ${colors.dot} shrink-0`} />
            <div className="flex-1 text-left min-w-0">
              <p className={`text-xs font-semibold ${colors.text} truncate`}>{regionLabel}</p>
              <p className="text-[10px] text-muted-foreground">Click to switch</p>
            </div>
            <ArrowLeftRight className="h-3 w-3 text-muted-foreground shrink-0" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {visibleNavItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t px-2 py-2 space-y-1">
        <NotificationBell collapsed={collapsed} />
        {!collapsed && user && (
          <div className="px-3 py-1.5">
            <p className="text-xs font-medium truncate">{user.name || user.email}</p>
            <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-red-500/70 hover:bg-red-500/10 hover:text-red-500 transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span>Sign Out</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
